/* global procurementEmailMembers setupWizardCurrencyCode */

import React, { useEffect, useState } from 'react';

import CreatableSelect from 'react-select/creatable';

import StepShell from './StepShell';

import FormInput from 'CommonComponents/FormInput';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

// Workspace-member options for the recipient pickers, sourced from the
// Blade-injected global (array of { name, email }). Read defensively in case
// the view omits it.
const members = typeof procurementEmailMembers !== 'undefined' ? procurementEmailMembers : [];
const memberOptions = members
    .filter((member) => member && member.email)
    .map((member) => ({ label: member.name ? `${member.name} <${member.email}>` : member.email, value: member.email }));

const currencyCode = typeof setupWizardCurrencyCode !== 'undefined' && setupWizardCurrencyCode ? setupWizardCurrencyCode : '';

function EmailRecipientsInput({ inputId, value, onChange, placeholder, disabled }) {
    const selected = (value || []).map((email) => ({ label: email, value: email }));

    return (
        <CreatableSelect
            isMulti
            inputId={inputId}
            isDisabled={disabled}
            className="react-select-container"
            classNamePrefix="react-select"
            options={memberOptions}
            value={selected}
            placeholder={placeholder}
            onChange={(values) => onChange((values || []).map((option) => option.value))}
            formatCreateLabel={(input) => `${$t('Add')} "${input}"`}
        />
    );
}

/**
 * The settings that belong to one workflow toggle, indented beneath it.
 */
function WorkflowChildren({ children }) {
    return <div className="tw-flex tw-flex-col tw-gap-y-4 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">{children}</div>;
}

/**
 * Procurement workflows. The approval-driven settings are only sent by the
 * server when the organisation holds the Approvals module, so their absence
 * from the data payload is what hides them — the update endpoint rejects them
 * outright.
 *
 * Each approval email sits under the workflow it belongs to, and is greyed out
 * while that workflow is off. Its value is still submitted, so switching the
 * workflow back on restores it as it was.
 */
export default function CommitStep({ step, urls, deferring, onDefer, onSaved }) {
    const [values, setValues] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            // Only the keys the server sent are held, so a setting the
            // organisation is not entitled to is never submitted back.
            .then(({ data }) => setValues('pr_required_above' in data ? { ...data, pr_required_above: data.pr_required_above ?? '' } : data))
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    const hasApprovals = !!values && 'pr_enabled' in values;
    const readOnly = step.read_only;

    // Laravel reports a bad array element as 'field.2', so an inline message
    // has to collect every error whose key starts with the field it belongs to.
    const errorFor = (field) =>
        Object.entries(errors)
            .filter(([key]) => key === field || key.startsWith(`${field}.`))
            .flatMap(([, messages]) => messages)
            .join(' ');

    const set = (field, value) => {
        setErrors({});
        setValues((current) => ({ ...current, [field]: value }));
    };

    const handleNext = () => {
        setSaving(true);
        setErrors({});

        const payload = {
            ...values,
            // A blank threshold is "no threshold", which the server stores as
            // null — an empty string would fail the numeric rule instead.
            ...(hasApprovals ? { pr_required_above: values.pr_required_above === '' ? null : Number(values.pr_required_above) } : {}),
        };

        axios
            .put(urls.update, payload)
            .then(({ data }) => onSaved(data))
            .catch((e) => {
                // No Formik form here to hand the errors to, so a validation
                // failure is surfaced as its own message and against the field
                // that was refused.
                const responseErrors = e.response?.data?.errors;
                setErrors(responseErrors ?? {});
                toast.error(responseErrors ? Object.values(responseErrors).flat().join(' ') : $t('Something went wrong. Please try again.'));
            })
            .finally(() => setSaving(false));
    };

    if (!values) {
        return (
            <StepShell step={step} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}} nextLabel={$t('Save and continue')}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    const setting = (field, label, description, disabled = false) => (
        <div className="tw-p-4 tw-rounded-lg tw-border tw-border-charcoal-15 tw-flex tw-flex-col tw-gap-y-2">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-4">
                <h4 className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-0">{label}</h4>
                <ToggleInput
                    noBottomSpacing={true}
                    // ToggleInput renders no visible label here, so the setting
                    // title is the control's accessible name.
                    aria-label={label}
                    checked={!!values[field]}
                    disabled={readOnly || disabled}
                    onChange={() => set(field, !values[field])}
                />
            </div>
            <p className="tw-text-2xs tw-text-charcoal-50 tw-mb-0">{description}</p>
            {!!errorFor(field) && <p className="tw-text-2xs tw-text-red tw-mb-0">{errorFor(field)}</p>}
        </div>
    );

    const recipients = (field, label, helper, disabled = false) => (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <label htmlFor={field} className="text-input__label">
                {label}
            </label>
            <EmailRecipientsInput
                inputId={field}
                value={values[field]}
                disabled={readOnly || disabled}
                onChange={(emails) => set(field, emails)}
                placeholder={$t('Add email addresses')}
            />
            {helper && <p className="tw-text-2xs tw-text-charcoal-50 tw-mb-0">{helper}</p>}
            {!!errorFor(field) && <p className="tw-text-2xs tw-text-red tw-mb-0">{errorFor(field)}</p>}
        </div>
    );

    return (
        <StepShell
            step={step}
            lede={$t(
                'Controls for committing spend and revenue. All off by default; switch them on when you are ready to control commitments through the platform.',
            )}
            deferring={deferring}
            saving={saving}
            onDefer={onDefer}
            onNext={handleNext}
            nextDisabled={readOnly}
            nextLabel={$t('Save and continue')}
        >
            <div className="tw-flex tw-flex-col tw-gap-y-4 tw-max-w-3xl">
                {hasApprovals && (
                    <>
                        {setting(
                            'pr_enabled',
                            $t('Turn on purchase requisitions (PRs)'),
                            $t('Raise and approve PRs against budget before spend is committed, so commitments are not made without budget cover.'),
                        )}

                        <WorkflowChildren>
                            {!!values.pr_enabled && (
                                <div className="tw-space-y-1.5">
                                    <FormInput
                                        id="pr_required_above"
                                        name="pr_required_above"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        // A project with no currency set yet would
                                        // otherwise be labelled "PR required above ()".
                                        label={currencyCode ? $t('PR required above ({{currency}})', { currency: currencyCode }) : $t('PR required above')}
                                        value={values.pr_required_above}
                                        placeholder={$t('Leave blank for no threshold')}
                                        disabled={readOnly}
                                        showError={!!errorFor('pr_required_above')}
                                        errorText={errorFor('pr_required_above')}
                                        onChange={(e) => set('pr_required_above', e.target.value)}
                                    />
                                    <p className="tw-text-2xs tw-text-charcoal-50 tw-mb-0">
                                        {$t('Commitments above this value must go through a PR. Shown in your local currency.')}
                                    </p>
                                </div>
                            )}

                            {setting(
                                'pr_approval_email_enabled',
                                $t('Email approved PRs to requestor and approvers'),
                                $t('Send a confirmation email when a PR is approved.'),
                                !values.pr_enabled,
                            )}
                            {!!values.pr_approval_email_enabled &&
                                recipients('pr_approval_email_recipients', $t('Additional PR recipients'), null, !values.pr_enabled)}
                        </WorkflowChildren>

                        {setting(
                            'ea_enabled',
                            $t('Turn on expenditure approvals (EAs)'),
                            $t(
                                'Approve expenditure before it is committed, ensure policies have been followed, supporting information is provided, conflicts are declared prior to commitments being made and contracts signed. Built-in workflows send these requests to the relevant people in your organisation to approve.',
                            ),
                        )}

                        <WorkflowChildren>
                            {setting(
                                'ea_approval_email_enabled',
                                $t('Email approved EAs to requestor and approvers'),
                                $t('Send a confirmation email when an EA is approved.'),
                                !values.ea_enabled,
                            )}
                            {!!values.ea_approval_email_enabled &&
                                recipients('ea_approval_email_recipients', $t('Additional EA recipients'), null, !values.ea_enabled)}
                        </WorkflowChildren>
                    </>
                )}

                {setting(
                    'expense_direct_commit_enabled',
                    $t('Turn on direct expenditure commitments (DCs)'),
                    $t('An abbreviated version of EAs for a faster way to create commitments.'),
                )}

                {hasApprovals && (
                    <WorkflowChildren>
                        {setting(
                            'dc_approval_email_enabled',
                            $t('Email approved direct commitments to requestor'),
                            $t('Send a confirmation email when a direct commitment is approved.'),
                            !values.expense_direct_commit_enabled,
                        )}
                        {!!values.dc_approval_email_enabled &&
                            recipients('dc_approval_email_recipients', $t('Additional DC recipients'), null, !values.expense_direct_commit_enabled)}
                    </WorkflowChildren>
                )}

                {setting(
                    'revenue_direct_commit_enabled',
                    $t('Turn on direct revenue commitments'),
                    $t('Record committed revenue directly as agreements are signed, the revenue equivalent of a DC.'),
                )}

                {recipients(
                    'supplier_request_recipients',
                    $t('Supplier request recipients'),
                    $t('These addresses are emailed when a user requests a new supplier be added to the ERP. Press enter after each address to add it.'),
                )}
            </div>
        </StepShell>
    );
}
